/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo, useState } from 'react';

import { jsx, cssMap as unboundCssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { LinkButton } from '@atlaskit/button/new';
import { IconTile } from '@atlaskit/icon';
import GlobeIcon from '@atlaskit/icon/core/globe';
import LinkIcon from '@atlaskit/icon/core/link';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import Image from '@atlaskit/image';
import Link from '@atlaskit/link';
import { Anchor, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import {
	type ActionItem,
	ActionName,
	Card,
	type ElementItem,
	ElementName,
	FooterBlock,
	MetadataBlock,
	SmartLinkPosition,
	SmartLinkSize,
	SmartLinkTheme,
	SnippetBlock,
	TitleBlock,
} from '@atlaskit/smart-card';
import { CardSSR } from '@atlaskit/smart-card/ssr';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { EMBEDDED_SOURCE_PRODUCTS } from './constants';
import type { KnowledgeCardSource } from './types';
import { extractUrlHostname } from './utils';

const messages = defineMessages({
	navigate_button: {
		id: 'search_ai.action_card_navigate_button',
		defaultMessage: 'Navigate',
		description: 'Navigate button text for action card',
	},
});

const unboundStyles = unboundCssMap({
	compactContainer: {
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'.loader-wrapper': {
			width: '100%',
		},
		backgroundColor: token('color.background.neutral.subtle'),
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		maxWidth: '360px',
		minHeight: '72px',
		margin: '0 auto',
		textDecoration: 'none',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			textDecoration: 'none',
			cursor: 'pointer',
		},
	},
	actionCardContainer: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		display: 'flex',
		alignItems: 'center',
		maxWidth: '360px',
		width: '100%',
		textDecoration: 'none',
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	actionCardButton: {
		textAlign: 'right',
	},
	fallbackContainer: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	embeddedCardContainer: {
		width: '100%',
		height: '100%',
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'.loader-wrapper': {
			height: '100%',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="embed-card-resolved-view"]': {
			boxSizing: 'border-box',
			margin: '0',
			border: `1px solid ${token('color.border')}`,
			borderRadius: token('radius.large'),
		},
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'.loader-wrapper > div[data-testid="embed-card-resolved-view"]::after': {
			outline: 'none',
		},
	},
	richSourceCardContainer: {
		width: '100%',
		height: '100%',
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'.loader-wrapper': {
			height: '100%',
		},
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& div[data-smart-link-container="true"]': {
			boxSizing: 'border-box',
			margin: '0',
			borderRadius: token('radius.large'),
			height: '100%',
		},
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& div[data-testid="smart-footer-block-resolved-view"]': {
			marginTop: 'auto',
		},
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-block-unauthorized-view-content"]': {
			marginBottom: token('space.100'),
		},
	},
	standardCardFallbackContainer: {
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		width: '100%',
		height: '185px',
		textDecoration: 'none',
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	titleBlockCss: {
		gap: token('space.100'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		"[data-smart-element='Title']": {
			fontWeight: token('font.weight.semibold'),
			color: token('color.link'),
		},
	},
	compactTitleBlockCss: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		"[data-smart-element='Title']": {
			font: token('font.heading.xsmall'),
			marginBottom: token('space.025'),
		},
	},
	footerBlockCss: {
		height: '1.5rem',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-end',
		alignSelf: 'stretch',
	},
});

function constructSourceUrl({
	source,
	searchSessionId,
}: {
	searchSessionId: string;
	source: KnowledgeCardSource;
}): string {
	if (source.product === 'Confluence' && !!searchSessionId) {
		const url = new URL(source.url);
		url.searchParams.append('search_id', searchSessionId);
		return url.toString();
	}
	return source.url;
}

export enum CardVariant {
	COMPACT = 'COMPACT',
	FULL = 'FULL',
	DROPDOWN_SOURCE = 'DROPDOWN_SOURCE',
	ACTION_CARD = 'ACTION_CARD',
}

interface KnowledgeSmartCardProps {
	index?: number;
	onClickHandler?: (params: any) => void;
	onError?: (error: any) => void;
	searchSessionId?: string;
	source: KnowledgeCardSource;
	variant?: CardVariant;
}

interface SmartCardInternalProps {
	index: number;
	onClickHandler: (params: any) => void;
	onError?: (error: any) => void;
	searchSessionId: string;
	source: KnowledgeCardSource;
}

const createCardClickHandler =
	(
		source: KnowledgeCardSource,
		index: number,
		searchSessionId: string,
		onClickHandler: (params: any) => void,
		sourceCardType: string,
	) =>
	(e: React.MouseEvent) => {
		onClickHandler?.({
			index,
			source,
			hostname: extractUrlHostname(source.url),
			sourceCardType,
		});
		e.preventDefault();
		window.open(constructSourceUrl({ source, searchSessionId }), '_blank', 'noopener, noreferrer');
	};

function SimpleFallbackCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
}: SmartCardInternalProps) {
	const handleClick = (e: React.MouseEvent) => {
		onClickHandler?.({
			index,
			source,
			hostname: extractUrlHostname(source.url),
			sourceCardType: 'compact',
		});
		e.preventDefault();
		window.open(constructSourceUrl({ source, searchSessionId }), '_blank', 'noopener, noreferrer');
	};

	return (
		<Anchor
			href={source.url}
			xcss={unboundStyles.compactContainer}
			onClick={handleClick}
			testId="knowledge-smart-card-fallback"
		>
			<Box xcss={unboundStyles.fallbackContainer}>
				<Stack space="space.050">
					<Text size="medium" weight="bold" maxLines={1}>
						{source.title || source.url}
					</Text>
					<Inline space="space.050" alignBlock="center">
						{source.iconUrl && (
							<Tile isInset={false} label="" size="xxsmall">
								<Image src={source.iconUrl} alt={source.product || ''} />
							</Tile>
						)}
						<Text size="small" color="color.text.subtlest" maxLines={1}>
							{source.product || ''}
						</Text>
					</Inline>
				</Stack>
			</Box>
		</Anchor>
	);
}

function ActionCard({ source, index, searchSessionId, onClickHandler }: SmartCardInternalProps) {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={unboundStyles.actionCardContainer} testId="knowledge-smart-card-action-card">
			<Stack space="space.200" grow="fill">
				<Inline space="space.150" alignBlock="center">
					<IconTile label="" icon={GlobeIcon} appearance="gray" size="medium" />
					<Stack space="space.025">
						<Text weight="bold" size="medium" maxLines={1}>
							{source.title}
						</Text>
						<Text size="small" color="color.text.subtlest" maxLines={1}>
							<Link href={source.url} target="_blank" rel="noopener noreferrer" appearance="subtle">
								{source.url}
							</Link>
						</Text>
					</Stack>
				</Inline>
				<Box xcss={unboundStyles.actionCardButton}>
					<LinkButton
						href={source.url}
						iconAfter={LinkExternalIcon}
						onClick={createCardClickHandler(
							source,
							index,
							searchSessionId,
							onClickHandler,
							'action-card',
						)}
					>
						{formatMessage(messages.navigate_button)}
					</LinkButton>
				</Box>
			</Stack>
		</Box>
	);
}

function CompactKnowledgeSmartCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
	onError,
}: SmartCardInternalProps) {
	const [hasError, setHasError] = useState(false);

	if (hasError) {
		return (
			<SimpleFallbackCard
				source={source}
				index={index}
				searchSessionId={searchSessionId}
				onClickHandler={onClickHandler}
			/>
		);
	}

	return (
		<Box xcss={unboundStyles.compactContainer} testId="knowledge-smart-card-compact">
			<CardSSR
				appearance="block"
				url={source.url}
				ui={{
					theme: SmartLinkTheme.Black,
					clickableContainer: true,
					hideBackground: true,
					hideElevation: true,
					size: SmartLinkSize.Medium,
					removeBlockRestriction: true,
				}}
				onClick={createCardClickHandler(source, index, searchSessionId, onClickHandler, 'compact')}
				onError={(err) => {
					setHasError(true);
					onError?.(err);
				}}
			>
				<TitleBlock
					maxLines={1}
					size={SmartLinkSize.Small}
					subtitle={[{ name: ElementName.Provider }]}
					position={SmartLinkPosition.Center}
					hideIcon={true}
					css={unboundStyles.compactTitleBlockCss}
				/>
			</CardSSR>
		</Box>
	);
}

function StandardKnowledgeSmartCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
	onError,
}: SmartCardInternalProps) {
	const [hasError, setHasError] = useState(false);

	const footerActions: ActionItem[] = useMemo(
		() => [
			{ name: ActionName.FollowAction, hideIcon: true },
			{ name: ActionName.PreviewAction, hideIcon: true },
			{ name: ActionName.DownloadAction, hideIcon: true },
		],
		[],
	);

	const topMetadata: ElementItem[] = [
		{ name: ElementName.AuthorGroup },
		{ name: ElementName.AssignedTo },
		{ name: ElementName.CreatedBy },
		{ name: ElementName.ModifiedOn },
	];

	const titleMetadata: ElementItem[] = [{ name: ElementName.State }];

	return (
		<Box xcss={unboundStyles.richSourceCardContainer} testId="knowledge-smart-card-standard">
			<Card
				appearance="block"
				url={source.url}
				ui={{ removeBlockRestriction: true }}
				fallbackComponent={() => (
					<Box xcss={unboundStyles.standardCardFallbackContainer}>
						<Inline space="space.100" alignBlock="center">
							<LinkIcon label="" color={token('color.icon')} />
							<Tooltip content={source.url}>
								<Anchor href={source.url} target="_blank">
									<Text weight="bold" size="medium" color="color.link" maxLines={1}>
										{source.url}
									</Text>
								</Anchor>
							</Tooltip>
						</Inline>
					</Box>
				)}
				onClick={createCardClickHandler(
					source,
					index,
					searchSessionId,
					onClickHandler,
					'rich-flexible',
				)}
				onError={(err) => {
					setHasError(true);
					onError?.(err);
				}}
			>
				{!hasError && (
					<React.Fragment>
						<TitleBlock maxLines={1} metadata={titleMetadata} css={unboundStyles.titleBlockCss} />
						<MetadataBlock primary={topMetadata} maxLines={1} />
						<SnippetBlock />
						<FooterBlock actions={footerActions} css={unboundStyles.footerBlockCss} />
					</React.Fragment>
				)}
			</Card>
		</Box>
	);
}

function EmbeddedKnowledgeSmartCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
	onError,
}: SmartCardInternalProps) {
	const [hasError, setHasError] = useState(false);

	return (
		<Box
			xcss={hasError ? unboundStyles.richSourceCardContainer : unboundStyles.embeddedCardContainer}
			testId="knowledge-smart-card-embedded"
		>
			<Card
				appearance={hasError ? 'block' : 'embed'}
				inheritDimensions={!hasError}
				frameStyle={hasError ? undefined : 'show'}
				url={source.url}
				fallbackComponent={() => (
					<Box xcss={unboundStyles.standardCardFallbackContainer}>
						<Inline space="space.100" alignBlock="center">
							<LinkIcon label="" color={token('color.icon')} />
							<Tooltip content={source.url}>
								<Anchor href={source.url} target="_blank">
									<Text weight="bold" size="medium" color="color.link" maxLines={1}>
										{source.url}
									</Text>
								</Anchor>
							</Tooltip>
						</Inline>
					</Box>
				)}
				onClick={createCardClickHandler(source, index, searchSessionId, onClickHandler, 'embedded')}
				onError={(err) => {
					setHasError(true);
					onError?.(err);
				}}
			/>
		</Box>
	);
}

function DropdownSourceSmartCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
	onError,
}: SmartCardInternalProps) {
	return (
		<Card
			appearance="block"
			ui={{
				theme: SmartLinkTheme.Black,
				clickableContainer: true,
				hideBackground: true,
				hideElevation: true,
				hidePadding: true,
				size: SmartLinkSize.Medium,
				removeBlockRestriction: true,
			}}
			url={source.url}
			onClick={createCardClickHandler(
				source,
				index,
				searchSessionId,
				onClickHandler,
				'dropdown-source',
			)}
			onError={onError || (() => {})}
		>
			<TitleBlock maxLines={1} size={SmartLinkSize.Medium} />
			<SnippetBlock maxLines={2} />
		</Card>
	);
}

export const KnowledgeSmartCard = ({
	source,
	variant,
	index,
	searchSessionId,
	onClickHandler,
	onError,
}: KnowledgeSmartCardProps): React.JSX.Element => {
	// Apply defaults for internal components
	const internalProps: SmartCardInternalProps = {
		onError: onError || (() => {}),
		source,
		index: index || 0,
		searchSessionId: searchSessionId || '',
		onClickHandler: onClickHandler || (() => {}),
	};

	if (variant === CardVariant.COMPACT) {
		return <CompactKnowledgeSmartCard {...internalProps} />;
	}

	if (variant === CardVariant.DROPDOWN_SOURCE) {
		return <DropdownSourceSmartCard {...internalProps} />;
	}

	if (variant === CardVariant.ACTION_CARD) {
		return <ActionCard {...internalProps} />;
	}

	const shouldUseEmbedded = source.product && source.product in EMBEDDED_SOURCE_PRODUCTS;

	if (shouldUseEmbedded) {
		return <EmbeddedKnowledgeSmartCard {...internalProps} />;
	}

	return <StandardKnowledgeSmartCard {...internalProps} />;
};
